import Decimal from 'decimal.js';

/** Public API base of the sellable OTP product (otp.vylo-tech.com). */
export const OTP_BASE_URL = 'https://otp.vylo-tech.com';
export const OTP_DOCS_URL = 'https://otp.vylo-tech.com/docs';

/**
 * USD money string → "$1,234.50". Minimum 2 decimals, up to 4 when the price
 * actually uses them (e.g. "$0.0025"), trailing zeros trimmed. decimal.js in,
 * never floats — the callers hand us the API's money strings as-is.
 */
export function fmtUsd(value: string | number | Decimal): string {
  const d = new Decimal(value || 0);
  // toFixed(4), then trim trailing zeros beyond the second decimal.
  const fixed = d.abs().toFixed(4).replace(/(\.\d{2}\d*?)0+$/, '$1');
  const [int = '0', frac = '00'] = fixed.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${d.isNegative() ? '-' : ''}$${grouped}.${frac}`;
}

/** House error convention — the Problem body carries `cause.code`. */
export function errCode(e: unknown): string | undefined {
  return (e as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
}

/**
 * Badge tone for an Evolution instance in the OTP context: only 'open' can
 * deliver, anything else means the client's sends will fail — 'connecting' and
 * 'close' are recoverable (warning), an unknown state reads as broken (danger).
 */
export function instanceStateVariant(state: string): 'success' | 'warning' | 'danger' {
  if (state === 'open') return 'success';
  if (state === 'connecting' || state === 'close') return 'warning';
  return 'danger';
}

/** "9647501234567" → "96475•••••567" — enough to recognise, not to dial. */
export function maskPhone(phone: string): string {
  if (phone.length <= 8) {
    return `${'•'.repeat(Math.max(0, phone.length - 2))}${phone.slice(-2)}`;
  }
  return `${phone.slice(0, 5)}${'•'.repeat(phone.length - 8)}${phone.slice(-3)}`;
}

/**
 * A ready-to-import Postman Collection (v2.1) for one client — base URL preset,
 * `apiKey` variable filled with the given key (the real key at create/rotate,
 * a placeholder otherwise). Mirrors the server's /postman.json builder.
 */
export function buildPostmanCollection(apiKey = 'otpsk_YOUR_API_KEY'): Record<string, unknown> {
  const jsonBody = (obj: Record<string, string>): Record<string, unknown> => ({
    mode: 'raw',
    raw: JSON.stringify(obj, null, 2),
    options: { raw: { language: 'json' } },
  });
  const url = (path: string): Record<string, unknown> => ({
    raw: `{{baseUrl}}${path}`,
    host: ['{{baseUrl}}'],
    path: path.replace(/^\//, '').split('/'),
  });
  return {
    info: {
      name: 'Vylo OTP API',
      description:
        'WhatsApp OTP and messaging as a service. Set the `apiKey` variable to your key (otpsk_...), then Send a code and Verify it, or Send a message from the same number and read the message log.',
      schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
    },
    auth: { type: 'bearer', bearer: [{ key: 'token', value: '{{apiKey}}', type: 'string' }] },
    variable: [
      { key: 'baseUrl', value: OTP_BASE_URL, type: 'string' },
      { key: 'apiKey', value: apiKey, type: 'string' },
      { key: 'phone', value: '9647501234567', type: 'string' },
      { key: 'message', value: 'Hello from Vylo.', type: 'string' },
    ],
    item: [
      {
        name: 'Send OTP',
        request: {
          method: 'POST',
          header: [{ key: 'Content-Type', value: 'application/json' }],
          body: jsonBody({ phone: '{{phone}}' }),
          url: url('/otp/send'),
        },
      },
      {
        name: 'Verify OTP',
        request: {
          method: 'POST',
          header: [{ key: 'Content-Type', value: 'application/json' }],
          body: jsonBody({ phone: '{{phone}}', code: '123456' }),
          url: url('/otp/verify'),
        },
      },
      {
        name: 'Send message',
        request: {
          method: 'POST',
          header: [{ key: 'Content-Type', value: 'application/json' }],
          body: jsonBody({ phone: '{{phone}}', message: '{{message}}' }),
          url: url('/messages/send'),
        },
      },
      { name: 'Message log', request: { method: 'GET', header: [], url: url('/messages') } },
      { name: 'Usage & balance', request: { method: 'GET', header: [], url: url('/usage') } },
    ],
  };
}

/** Trigger a client-side JSON file download (no network round-trip). */
export function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}
